import { useEffect, useState } from 'react';
import { getFile } from '@/api/query/file';

const imageCache = new Map();
const pendingLoads = new Map();

export const normalizeFileId = (fileId) => {
	if (fileId == null || fileId === '') {
		return null;
	}

	if (typeof fileId === 'string') {
		const trimmed = fileId.trim();

		if (!trimmed || trimmed === '[object Object]') {
			return null;
		}

		return trimmed;
	}

	if (typeof fileId !== 'object') {
		return null;
	}

	if (fileId._id != null && fileId._id !== fileId) {
		return normalizeFileId(fileId._id);
	}

	if (typeof fileId.toHexString === 'function') {
		return fileId.toHexString();
	}

	if (
		typeof fileId.toString === 'function'
		&& fileId.toString !== Object.prototype.toString
	) {
		const asString = fileId.toString();

		if (asString && asString !== '[object Object]') {
			return asString;
		}
	}

	return null;
};

const loadImage = async (normalizedFileId) => {
	if (imageCache.has(normalizedFileId)) {
		return imageCache.get(normalizedFileId);
	}

	if (pendingLoads.has(normalizedFileId)) {
		return pendingLoads.get(normalizedFileId);
	}

	const request = getFile(normalizedFileId)
		.then((currentImage) => {
			const src = currentImage?.data ?? null;

			if (src) {
				imageCache.set(normalizedFileId, src);
			}

			return src;
		})
		.catch(() => null)
		.finally(() => {
			pendingLoads.delete(normalizedFileId);
		});

	pendingLoads.set(normalizedFileId, request);

	return request;
};

const useImageLoader = (fileId) => {
	const normalizedFileId = normalizeFileId(fileId);
	const cachedSrc = normalizedFileId ? imageCache.get(normalizedFileId) : null;
	const [imageSrc, setImageSrc] = useState(cachedSrc);
	const [isLoading, setIsLoading] = useState(Boolean(normalizedFileId && !cachedSrc));

	useEffect(() => {
		if (!normalizedFileId) {
			setImageSrc(null);
			setIsLoading(false);
			return;
		}

		const cached = imageCache.get(normalizedFileId);

		if (cached) {
			setImageSrc(cached);
			setIsLoading(false);
			return;
		}

		let isActive = true;
		setIsLoading(true);
		setImageSrc(null);

		loadImage(normalizedFileId).then((src) => {
			if (!isActive) {
				return;
			}

			setImageSrc(src);
			setIsLoading(false);
		});

		return () => {
			isActive = false;
		};
	}, [normalizedFileId]);

	return { imageSrc, isLoading };
};

export default useImageLoader;
